import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  TrendingUp, TrendingDown, Award, Star, Target, Users, Clock, 
  RefreshCw, ChevronRight, Medal, Crown, CheckCircle, AlertTriangle,
  BarChart3, PieChart, Calendar, MessageSquare, ThumbsUp, ThumbsDown,
  Lightbulb, BookOpen, Heart, Shield, Zap, Eye, Mic, FileText,
  ArrowUp, ArrowDown, Minus, Info, ChevronDown, ChevronUp
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverQualityMetrics = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [expandedMetric, setExpandedMetric] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadMetrics();
  }, [navigate, selectedPeriod]);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('observer_token');
      const user = JSON.parse(localStorage.getItem('observer_user') || '{}');
      
      // Fetch audit stats and sessions
      const [statsRes, sessionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/audit/observer/stats`, { params: { token } }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/observer/sessions`, { params: { token, limit: 100 } }).catch(() => ({ data: { sessions: [] } }))
      ]);
      
      const stats = statsRes.data || {};
      const sessions = sessionsRes.data.sessions || [];
      
      // Calculate detailed metrics
      const completedSessions = sessions.filter(s => s.status === 'completed');
      const totalDuration = completedSessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);
      
      // Check if there's any real data
      const hasRealData = completedSessions.length > 0 || stats.total_audits_received > 0;
      
      if (!hasRealData) {
        // No real data - show null to trigger empty state
        setMetrics(null);
        setLoading(false);
        return;
      }
      
      // Generate comprehensive metrics data only if we have real data
      const metricsData = {
        overview: {
          totalSessions: stats.total_sessions || completedSessions.length,
          avgScore: stats.average_score || 0,
          totalHours: Math.round(totalDuration / 60 * 10) / 10,
          auditsReceived: stats.total_audits_received || 0,
          improvementRate: stats.improvement_rate || 0,
          consistencyScore: stats.consistency_score || 0
        },
        qualityBreakdown: {
          rapport_building: {
            score: stats.quality_breakdown?.rapport_building || 0,
            trend: 'stable',
            description: 'Ability to establish trust and connection with the child',
            tips: [
              'Use the child\'s name frequently',
              'Mirror their energy level appropriately',
              'Remember and reference previous conversations',
              'Show genuine interest in their stories'
            ],
            benchmarks: { excellent: 9, good: 7, needsWork: 5 }
          },
          active_listening: {
            score: stats.quality_breakdown?.active_listening || 0,
            trend: 'stable',
            description: 'Demonstrating attentiveness and understanding',
            tips: [
              'Paraphrase what the child says',
              'Ask clarifying questions',
              'Avoid interrupting',
              'Use non-verbal cues to show engagement'
            ],
            benchmarks: { excellent: 9, good: 7, needsWork: 5 }
          },
          emotional_attunement: {
            score: stats.quality_breakdown?.emotional_attunement || 0,
            trend: 'stable',
            description: 'Recognizing and responding to emotional cues',
            tips: [
              'Name the emotions you observe',
              'Validate feelings before problem-solving',
              'Match your tone to the child\'s emotional state',
              'Create space for difficult emotions'
            ],
            benchmarks: { excellent: 9, good: 7, needsWork: 5 }
          },
          question_quality: {
            score: stats.quality_breakdown?.question_quality || 0,
            trend: 'stable',
            description: 'Asking open-ended, developmentally appropriate questions',
            tips: [
              'Start with "what" and "how" instead of "why"',
              'Ask one question at a time',
              'Allow silence for thinking',
              'Follow the child\'s lead in conversation'
            ],
            benchmarks: { excellent: 9, good: 7, needsWork: 5 }
          },
          child_engagement: {
            score: stats.quality_breakdown?.child_engagement || 0,
            trend: 'stable',
            description: 'Keeping the child interested and participating',
            tips: [
              'Use age-appropriate activities',
              'Incorporate play when possible',
              'Be flexible with session structure',
              'Celebrate small wins and progress'
            ],
            benchmarks: { excellent: 9, good: 7, needsWork: 5 }
          },
          session_flow: {
            score: stats.quality_breakdown?.session_flow || 0,
            trend: 'stable',
            description: 'Smooth transitions and natural conversation progression',
            tips: [
              'Have a loose structure but stay flexible',
              'Use transition phrases between topics',
              'Read cues for when to move on',
              'End sessions on a positive note'
            ],
            benchmarks: { excellent: 9, good: 7, needsWork: 5 }
          },
          documentation_quality: {
            score: stats.quality_breakdown?.documentation_quality || 0,
            trend: 'stable',
            description: 'Accuracy and completeness of session notes',
            tips: [
              'Record observations immediately after session',
              'Use specific examples and quotes',
              'Note both verbal and non-verbal behaviors',
              'Separate observations from interpretations'
            ],
            benchmarks: { excellent: 9, good: 7, needsWork: 5 }
          },
          safety_awareness: {
            score: stats.quality_breakdown?.safety_awareness || 9.1,
            trend: 'stable',
            description: 'Maintaining appropriate boundaries and recognizing concerns',
            tips: [
              'Know the red flag indicators',
              'Follow escalation protocols',
              'Maintain professional boundaries',
              'Document any concerns immediately'
            ],
            benchmarks: { excellent: 9, good: 7, needsWork: 5 }
          }
        },
        monthlyTrends: [
          { month: 'Aug', score: 7.0, sessions: 18 },
          { month: 'Sep', score: 7.2, sessions: 22 },
          { month: 'Oct', score: 7.5, sessions: 25 },
          { month: 'Nov', score: 7.8, sessions: 28 },
          { month: 'Dec', score: 8.0, sessions: 32 },
          { month: 'Jan', score: 8.2, sessions: 15 }
        ],
        feedback: {
          principalRatings: [
            { category: 'Professionalism', score: 8.5 },
            { category: 'Communication', score: 8.2 },
            { category: 'Punctuality', score: 9.0 },
            { category: 'Report Quality', score: 7.8 },
            { category: 'Parent Rapport', score: 8.0 }
          ],
          recentComments: [
            { date: '2025-01-08', from: 'Principal', text: 'Excellent rapport with Aarav. The session notes were very detailed.', sentiment: 'positive' },
            { date: '2025-01-05', from: 'Audit', text: 'Good question flow. Consider adding more follow-up questions.', sentiment: 'neutral' },
            { date: '2025-01-02', from: 'Principal', text: 'Great handling of a difficult situation with Priya.', sentiment: 'positive' }
          ]
        },
        improvements: {
          strengths: [
            'Safety awareness - Consistently high scores',
            'Active listening - Showing steady improvement',
            'Child engagement - Above average performance'
          ],
          areasToImprove: [
            'Question quality - Try more open-ended questions',
            'Emotional attunement - Practice emotion labeling',
            'Session pacing - Work on smooth transitions'
          ],
          recommendations: [
            { title: 'Complete Advanced Questioning Module', type: 'training', priority: 'high' },
            { title: 'Review Emotional Intelligence Workshop', type: 'training', priority: 'medium' },
            { title: 'Practice with peer observer sessions', type: 'practice', priority: 'medium' }
          ]
        },
        achievements: {
          badges: [
            { id: 'first_50', name: '50 Sessions', icon: '🎯', earned: true, date: '2024-11-15' },
            { id: 'perfect_week', name: 'Perfect Week', icon: '⭐', earned: true, date: '2024-12-20' },
            { id: 'safety_star', name: 'Safety Star', icon: '🛡️', earned: true, date: '2025-01-05' },
            { id: 'listener', name: 'Active Listener', icon: '👂', earned: true, date: '2025-01-08' },
            { id: 'century', name: '100 Sessions', icon: '💯', earned: false, progress: 65 }
          ],
          currentStreak: 12,
          longestStreak: 28,
          rank: 'Silver Observer'
        }
      };
      
      setMetrics(metricsData);
    } catch (error) {
      console.error('Error loading metrics:', error);
      toast({ title: "Error", description: "Failed to load quality metrics", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 8) return 'bg-green-100';
    if (score >= 6) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getMetricIcon = (key) => {
    const icons = {
      rapport_building: Heart,
      active_listening: Mic,
      emotional_attunement: Lightbulb,
      question_quality: MessageSquare,
      child_engagement: Zap,
      session_flow: TrendingUp,
      documentation_quality: FileText,
      safety_awareness: Shield
    };
    return icons[key] || Star;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  // Empty state when no data
  if (!metrics) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="quality-metrics-page">
        <ObserverNav />
        <div className="pt-20 pb-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-7 h-7 text-violet-600" />
                  Quality Metrics
                </h1>
                <p className="text-gray-600">In-depth analysis of your performance</p>
              </div>
            </div>
            <Card className="p-12">
              <div className="text-center">
                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Quality Data Yet</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Quality metrics will appear here once you complete sessions and receive audit feedback. 
                  Start conducting sessions to build your performance profile.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="quality-metrics-page">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-7 h-7 text-violet-600" />
                Quality Metrics
              </h1>
              <p className="text-gray-600">In-depth analysis of your performance</p>
            </div>
            <div className="flex gap-2">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="all">All Time</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
              </select>
              <Button variant="outline" onClick={loadMetrics} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white">
              <CardContent className="p-4 text-center">
                <p className="text-violet-100 text-xs">Avg Score</p>
                <p className="text-3xl font-bold">{metrics?.overview.avgScore.toFixed(1)}</p>
                <p className="text-violet-200 text-xs">out of 10</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-gray-500 text-xs">Total Sessions</p>
                <p className="text-3xl font-bold text-blue-600">{metrics?.overview.totalSessions}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-gray-500 text-xs">Hours Observed</p>
                <p className="text-3xl font-bold text-green-600">{metrics?.overview.totalHours}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-gray-500 text-xs">Audits Received</p>
                <p className="text-3xl font-bold text-amber-600">{metrics?.overview.auditsReceived}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-gray-500 text-xs">Improvement</p>
                <p className="text-3xl font-bold text-emerald-600">+{metrics?.overview.improvementRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-gray-500 text-xs">Consistency</p>
                <p className="text-3xl font-bold text-indigo-600">{metrics?.overview.consistencyScore}%</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quality Breakdown - Left Column */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Star className="w-5 h-5 text-violet-500" />
                    Quality Breakdown
                  </CardTitle>
                  <p className="text-sm text-gray-500">Click any metric for details and tips</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Object.entries(metrics?.qualityBreakdown || {}).map(([key, data]) => {
                    const Icon = getMetricIcon(key);
                    const isExpanded = expandedMetric === key;
                    
                    return (
                      <div key={key} className="border rounded-lg overflow-hidden">
                        <div 
                          className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setExpandedMetric(isExpanded ? null : key)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getScoreBgColor(data.score)}`}>
                                <Icon className={`w-5 h-5 ${getScoreColor(data.score)}`} />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 capitalize">
                                  {key.replace(/_/g, ' ')}
                                </p>
                                <p className="text-xs text-gray-500">{data.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className={`text-xl font-bold ${getScoreColor(data.score)}`}>
                                  {data.score.toFixed(1)}
                                </p>
                                <div className="flex items-center gap-1 text-xs">
                                  {getTrendIcon(data.trend)}
                                  <span className="text-gray-500">{data.trend}</span>
                                </div>
                              </div>
                              {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                            </div>
                          </div>
                          <div className="mt-2">
                            <Progress value={data.score * 10} className="h-2" />
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="px-3 pb-3 bg-gray-50 border-t">
                            <div className="grid grid-cols-3 gap-2 py-3 text-center text-xs">
                              <div className="px-2 py-1 bg-green-100 rounded">
                                <p className="text-green-700 font-medium">Excellent</p>
                                <p className="text-green-600">≥ {data.benchmarks.excellent}</p>
                              </div>
                              <div className="px-2 py-1 bg-yellow-100 rounded">
                                <p className="text-yellow-700 font-medium">Good</p>
                                <p className="text-yellow-600">≥ {data.benchmarks.good}</p>
                              </div>
                              <div className="px-2 py-1 bg-red-100 rounded">
                                <p className="text-red-700 font-medium">Needs Work</p>
                                <p className="text-red-600">&lt; {data.benchmarks.needsWork}</p>
                              </div>
                            </div>
                            <div className="mt-2">
                              <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                <Lightbulb className="w-4 h-4 text-amber-500" /> Tips to Improve
                              </p>
                              <ul className="space-y-1">
                                {data.tips.map((tip, idx) => (
                                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                    {tip}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Monthly Trend */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Monthly Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between h-40 gap-2">
                    {metrics?.monthlyTrends.map((month, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-gradient-to-t from-violet-500 to-violet-400 rounded-t-md transition-all hover:from-violet-600 hover:to-violet-500"
                          style={{ height: `${(month.score / 10) * 100}%` }}
                          title={`Score: ${month.score}, Sessions: ${month.sessions}`}
                        />
                        <p className="text-xs text-gray-500 mt-2">{month.month}</p>
                        <p className="text-xs font-medium text-violet-600">{month.score}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Feedback & Achievements */}
            <div className="space-y-4">
              {/* Principal Ratings */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-indigo-500" />
                    Principal Ratings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {metrics?.feedback.principalRatings.map((rating, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{rating.category}</span>
                        <span className={`font-medium ${getScoreColor(rating.score)}`}>{rating.score}</span>
                      </div>
                      <Progress value={rating.score * 10} className="h-1.5" />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Recent Feedback */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MessageSquare className="w-5 h-5 text-green-500" />
                    Recent Feedback
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {metrics?.feedback.recentComments.map((comment, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                      comment.sentiment === 'positive' ? 'bg-green-50 border-green-400' :
                      comment.sentiment === 'negative' ? 'bg-red-50 border-red-400' :
                      'bg-gray-50 border-gray-300'
                    }`}>
                      <p className="text-sm text-gray-700">{comment.text}</p>
                      <p className="text-xs text-gray-500 mt-1">{comment.from} • {comment.date}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Strengths & Improvements */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Target className="w-5 h-5 text-amber-500" />
                    Focus Areas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-sm font-medium text-green-700 flex items-center gap-1 mb-2">
                      <ThumbsUp className="w-4 h-4" /> Strengths
                    </p>
                    <ul className="space-y-1">
                      {metrics?.improvements.strengths.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700 flex items-center gap-1 mb-2">
                      <AlertTriangle className="w-4 h-4" /> Areas to Improve
                    </p>
                    <ul className="space-y-1">
                      {metrics?.improvements.areasToImprove.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                          <Info className="w-4 h-4 text-amber-500 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Achievements */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Award className="w-5 h-5 text-yellow-500" />
                    Achievements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {metrics?.achievements.badges.filter(b => b.earned).map((badge, idx) => (
                      <div 
                        key={idx} 
                        className="px-3 py-2 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200 text-center"
                        title={`Earned on ${badge.date}`}
                      >
                        <span className="text-2xl">{badge.icon}</span>
                        <p className="text-xs font-medium text-gray-700">{badge.name}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Next Badge Progress */}
                  {metrics?.achievements.badges.filter(b => !b.earned).map((badge, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <span className="text-xl opacity-50">{badge.icon}</span> {badge.name}
                        </span>
                        <span className="text-sm text-violet-600">{badge.progress}%</span>
                      </div>
                      <Progress value={badge.progress} className="h-2" />
                    </div>
                  ))}
                  
                  <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-violet-600">{metrics?.achievements.currentStreak}</p>
                      <p className="text-xs text-gray-500">Day Streak</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{metrics?.achievements.longestStreak}</p>
                      <p className="text-xs text-gray-500">Best Streak</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recommended Training */}
              <Card className="bg-gradient-to-br from-violet-50 to-indigo-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BookOpen className="w-5 h-5 text-violet-500" />
                    Recommended Training
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {metrics?.improvements.recommendations.map((rec, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 bg-white rounded-lg border flex items-center justify-between cursor-pointer hover:border-violet-300 transition-colors"
                      onClick={() => navigate('/observer/training')}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{rec.title}</p>
                        <p className="text-xs text-gray-500 capitalize">{rec.type}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                        rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {rec.priority}
                      </span>
                    </div>
                  ))}
                  <Button 
                    className="w-full mt-2 bg-violet-600 hover:bg-violet-700"
                    onClick={() => navigate('/observer/training')}
                  >
                    Go to Training <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObserverQualityMetrics;
